// The signed-in person's account: name, organizations, password and sessions, alert email.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorText } from "../api/client";
import { useUI } from "../app/context";
import { keys, useSession } from "../app/queries";
import { Face } from "../components/Badge";
import { PasswordDialog } from "../components/PasswordDialog";

interface AccountInfo {
  name: string;
  email: string;
  initials: string;
  created_at: string;
  superuser: boolean;
  memberships: { id: number; name: string; role: "admin" | "member"; current: boolean }[];
  sessions: number;
  email_alerts: boolean;
  alert_email: boolean;
}

export function Account() {
  const me = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useUI();
  const account = useQuery({ queryKey: ["account"], queryFn: () => api<AccountInfo>("/api/account") });
  const [name, setName] = useState("");
  const [password, setPassword] = useState(false);
  useEffect(() => {
    if (account.data) setName(account.data.name);
  }, [account.data]);
  if (!account.data) return <p className="empty">Loading…</p>;
  const a = account.data;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("/api/account", { method: "PATCH", body: { name } });
      await Promise.all([client.invalidateQueries({ queryKey: keys.me }), client.invalidateQueries({ queryKey: ["account"] })]);
      showToast("Name saved.");
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const signOutOthers = async () => {
    const r = await api<{ ended: number }>("/api/account/sign-out-others", { method: "POST" });
    showToast(`Signed out ${r.ended} other session${r.ended === 1 ? "" : "s"}.`);
    await client.invalidateQueries({ queryKey: ["account"] });
  };
  const switchTo = async (id: number) => {
    await api("/api/tenant", { method: "POST", body: { tenant_id: id } });
    await client.resetQueries();
    navigate("/brief");
  };
  return (
    <div className="admin account">
      <div className="top">
        <div className="date">Account</div>
      </div>
      <div className="account-head">
        <Face id={me.user.id} text={a.initials} big />
        <div>
          <h1>{a.name}</h1>
          <p className="lede">
            {a.email}
            {a.superuser ? " · Superuser" : ""}
          </p>
        </div>
      </div>
      <section>
        <h2>Profile</h2>
        <form className="inline" id="acct-form" style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) auto" }} onSubmit={save}>
          <label>
            <span>Name</span>
            <input id="acct-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="name" />
          </label>
          <label>
            <span>Email</span>
            <input value={a.email} disabled aria-describedby="acct-email-note" />
          </label>
          <button className="btn primary">Save</button>
        </form>
        <p className="muted-s" id="acct-email-note">
          Your email is your sign-in. To change it, ask {a.superuser ? "another superuser" : "your admin"}.
        </p>
      </section>
      <section>
        <h2>Organizations</h2>
        {a.memberships.length ? (
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Your role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {a.memberships.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.role === "admin" ? "Admin" : "Member"}</td>
                  <td className="actions">
                    {m.current ? (
                      <span className="muted-s">Current</span>
                    ) : (
                      <button className="btn" data-switch={m.id} onClick={() => switchTo(m.id)}>
                        Switch to
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted-s">
            {a.superuser
              ? "You are not a member of any organization; as a superuser you can switch into any of them without taking a seat."
              : "You are not part of an organization yet."}
          </p>
        )}
        <p className="muted-s">Each organization pays for your seat separately. Removing you from one doesn't affect the others.</p>
      </section>
      <section>
        <h2>Sign-in and security</h2>
        <div className="acct-row">
          <div>
            <b>Password</b>
            <p className="muted-s">At least 10 characters. Changing it signs out your other devices.</p>
          </div>
          <button className="btn" id="acct-password" onClick={() => setPassword(true)}>
            Change password
          </button>
        </div>
        <div className="acct-row">
          <div>
            <b>Signed-in devices</b>
            <p className="muted-s">
              {a.sessions} active session{a.sessions === 1 ? "" : "s"}, including this one. Sessions last 14 days from last use.
            </p>
          </div>
          <button className="btn" id="acct-others" disabled={a.sessions <= 1} onClick={signOutOthers}>
            Sign out other devices
          </button>
        </div>
      </section>
      <section>
        <h2>Alerts</h2>
        <div className="acct-row">
          <div>
            <b>Alert email</b>
            <p className="muted-s">
              {a.email_alerts
                ? a.alert_email
                  ? `On: alerts are emailed to ${a.email}.`
                  : "Off: alerts appear only in GovChew."
                : "Email isn’t set up on this server; alerts appear in GovChew only."}
            </p>
          </div>
          <Link className="btn" to="/alerts/settings">
            Alert settings
          </Link>
        </div>
      </section>
      <p className="muted-s">Member since {new Date(a.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.</p>
      <PasswordDialog open={password} onClose={() => setPassword(false)} />
    </div>
  );
}
